export type AiPlanTabKey =
  | "taggingPlan"
  | "enhancedAssessmentPlan"
  | "objectToObjectPlan"
  | "triagePlan";

export type TaggingPlanRow = {
  id: string;
  /** API target_type code (e.g. insight, rtq). */
  objectType: string;
  /**
   * Reference Dimension value codes (`a,b` or `__all__`), hydrated from
   * `reference_items` (or legacy `sub_type`).
   */
  objectSubType: string;
  /** Display name from per-tenant types API. */
  objectTypeName: string;
  /** Display name for Reference Dimension values (comma-separated). */
  objectSubTypeName: string;
  /** Reference Dimension concept code (`kind=reference`) — first of multi. */
  referenceConceptCode: string;
  /** Reference Dimension display label — first of multi. */
  referenceConceptDisplayName: string;
  /**
   * All Reference Dimension selections (`reference_items`).
   * Empty when Object-only.
   */
  referenceDimensions: Array<{
    conceptCode: string;
    conceptDisplayName: string;
    values: Array<{ code: string; name: string; description?: string }>;
    allValues: boolean;
  }>;
  conceptCode: string;
  concept: string;
  values: string;
  /** Assessment Name — API `name`. */
  name: string;
  /** Assessment Template — API `assessment_type`. */
  assessmentType: string;
  /** Assessment Prompt — API `prompt` (legacy field name `description` in UI state). */
  description: string;
  enabled: boolean;
  allValues: boolean;
  conceptValues: Array<{
    code: string;
    name: string;
    description: string;
  }>;
  /** UI Tagging Approach (hydrated from API `tagging_approach`). */
  taggingApproach: string;
  /** UI Execution (independent of API `layer`; still seeded from layer on hydrate). */
  execution: string;
  /** Explicit API `layer` (int ≥ 0) from create/edit Layer field. */
  layer: number;
  /** UI Assessment Template label (hydrated from API `assessment_type`). */
  assessmentTemplate: string;
  /** Update Assessment Record dimension concept (`kind=assessment_record`). */
  updateAssessmentRecordConcept: string;
  /** Update Assessment Record value selections. */
  updateAssessmentRecords: string[];
};

export const AI_PLAN_PATH = "/plans-and-automations";
export const AI_PLAN_ADD_ITEMS_PATH =
  "/plan-and-automation/add-assessment-plan-items";
export const AI_PLAN_EDIT_ITEMS_PATH =
  "/plan-and-automation/edit-assessment-plan-items/:planId";
export const AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH =
  "/plan-and-automation/add-enhanced-tagging-rule";
export const AI_PLAN_EDIT_ENHANCED_TAGGING_RULE_PATH =
  "/plan-and-automation/edit-enhanced-tagging-rule/:ruleId";
export const AI_PLAN_ADD_TRIAGE_RULE_PATH =
  "/plan-and-automation/add-triage-rule";
export const AI_PLAN_EDIT_TRIAGE_RULE_PATH =
  "/plan-and-automation/edit-triage-rule/:ruleId";
export const AI_PLAN_ADD_OTO_PLAN_PATH =
  "/plan-and-automation/add-object-to-object-plan";
export const AI_PLAN_EDIT_OTO_PLAN_PATH =
  "/plan-and-automation/edit-object-to-object-plan/:planId";

export function buildEditAssessmentPlanItemsPath(planId: string): string {
  return `/plan-and-automation/edit-assessment-plan-items/${encodeURIComponent(planId)}`;
}

export function buildEditEnhancedTaggingRulePath(ruleId: string): string {
  return `/plan-and-automation/edit-enhanced-tagging-rule/${encodeURIComponent(ruleId)}`;
}

export function buildEditOtoPlanPath(planId: string): string {
  return `/plan-and-automation/edit-object-to-object-plan/${encodeURIComponent(planId)}`;
}

export function buildEditTriageRulePath(ruleId: string): string {
  return `/plan-and-automation/edit-triage-rule/${encodeURIComponent(ruleId)}`;
}

/** Sentinel option for selecting all concept values in Tagging Plan forms. */
export const CONCEPT_VALUE_ALL_OPTION = {
  id: "__all__",
  code: "__all__",
  name: "All",
} as const;

export const AI_PLAN_TAB_QUERY_KEY = "tab";

/** Legacy tab key kept for URL compatibility. */
const LEGACY_DIMENSION_WEIGHT_TAB = "dimensionWeightSettings";

export function normalizeAiPlanTabKey(
  value: string | null | undefined,
): AiPlanTabKey | null {
  if (value === LEGACY_DIMENSION_WEIGHT_TAB) return "objectToObjectPlan";
  if (
    value === "taggingPlan" ||
    value === "enhancedAssessmentPlan" ||
    value === "objectToObjectPlan" ||
    value === "triagePlan"
  ) {
    return value;
  }
  return null;
}

export function isAiPlanTabKey(
  value: string | null | undefined,
): value is AiPlanTabKey {
  return normalizeAiPlanTabKey(value) !== null;
}

export function buildAiPlanPath(tab?: AiPlanTabKey): string {
  if (!tab || tab === "taggingPlan") {
    return AI_PLAN_PATH;
  }
  return `${AI_PLAN_PATH}?${AI_PLAN_TAB_QUERY_KEY}=${tab}`;
}

export const AI_PLAN_TAB_LABELS: Record<
  AiPlanTabKey,
  { line1: string; line2?: string }
> = {
  taggingPlan: { line1: "Classification" },
  enhancedAssessmentPlan: { line1: "Conditional Classification Rule" },
  objectToObjectPlan: { line1: "Object to Object" },
  triagePlan: { line1: "Triage Plan" },
};
